import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API_URL, buildKioskAuthHeaders, getStoredAuthToken } from './api';
import { canonicalPublicPath, reservationOptionsContext } from './addonNavigation';
import ReservationSettings from './ReservationSettings';

const STATUS_LABELS = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  seated: 'Installée',
  completed: 'Terminée',
  cancelled: 'Annulée',
  no_show: 'No-show',
};

const SERVICE_LABELS = {
  lunch: 'Midi',
  dinner: 'Soir',
};

function CaisseReservationsView({ embedded = false, onBack = null }) {
  const location = useLocation();
  const optionsContext = !embedded ? reservationOptionsContext(location) : null;
  const requestSequence = useRef(0);
  const [publicSlug, setPublicSlug] = useState(null);
  const [canConfigure, setCanConfigure] = useState(false);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [dateValue, setDateValue] = useState(today);
  const [status, setStatus] = useState('all');
  const [service, setService] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [error, setError] = useState('');
  const reservationClientPath = canonicalPublicPath('beresa', publicSlug);

  const loadReservations = async (signal) => {
    const sequence = ++requestSequence.current, expectedToken = getStoredAuthToken();
    setPublicSlug(null);
    try {
      setError('');
      const params = new URLSearchParams({
        date: dateValue,
        status,
        service,
        search,
      });
      const response = await fetch(`${API_URL}/api/caisse/reservations?${params.toString()}`, {
        headers: buildKioskAuthHeaders(), signal, cache: 'no-store',
      });
      const payload = await response.json().catch(() => ({}));
      if (signal?.aborted || sequence !== requestSequence.current) return;
      if (getStoredAuthToken() !== expectedToken) throw new Error('Votre session a changé. Rechargez les réservations.');
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement impossible.');
      }
      setItems(Array.isArray(payload?.items) ? payload.items : []);
      setStatusCounts(payload?.statusCounts || {});
      setPublicSlug(canonicalPublicPath('beresa', payload?.publicSlug) ? payload.publicSlug : null);
      setCanConfigure(payload?.canConfigure === true);
    } catch (loadError) {
      if (signal?.aborted || sequence !== requestSequence.current) return;
      setPublicSlug(null);
      setCanConfigure(false);
      setError(loadError.message || 'Chargement impossible.');
      setItems([]);
      setStatusCounts({});
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadReservations(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue, status, service, search]);

  const updateStatus = async (item, nextStatus) => {
    try {
      const headers = buildKioskAuthHeaders();
      headers.set('Content-Type', 'application/json');
      const response = await fetch(`${API_URL}/api/caisse/reservations/${item.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: nextStatus }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Mise à jour impossible.');
      }
      await loadReservations();
    } catch (updateError) {
      setError(updateError.message || 'Mise à jour impossible.');
    }
  };

  return (
    <main className={`bc-resa-admin-page ${embedded ? 'is-embedded' : ''}`}>
      <section className="bc-resa-admin-shell">
        <div className="bc-resa-admin-head">
          <div>
            <span>{optionsContext ? `Espace ${optionsContext.name}` : 'Gestion caisse'}</span>
            <h1>Réservations</h1>
          </div>
          <div className="bc-resa-admin-head-actions">
            {embedded ? (
              <button type="button" onClick={() => onBack && onBack()} className="bc-resa-btn bc-resa-btn-secondary">
                Retour caisse
              </button>
            ) : (
              <Link to={optionsContext?.returnPath || '/caisse'} className="bc-resa-btn bc-resa-btn-secondary">{optionsContext ? 'Retour à Mes options' : 'Retour caisse'}</Link>
            )}
            {reservationClientPath && <Link to={reservationClientPath} className="bc-resa-btn bc-resa-btn-secondary" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Page client</Link>}
          </div>
        </div>

        <ReservationSettings allowed={canConfigure} />

        <div className="bc-resa-admin-filters">
          <input aria-label="Date des réservations" type="date" value={dateValue} onChange={(e) => setDateValue(e.target.value)} />
          <select aria-label="Statut des réservations" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">Tous statuts</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmée</option>
            <option value="seated">Installée</option>
            <option value="completed">Terminée</option>
            <option value="cancelled">Annulée</option>
            <option value="no_show">No-show</option>
          </select>
          <select aria-label="Service des réservations" value={service} onChange={(e) => setService(e.target.value)}>
            <option value="all">Tous services</option>
            <option value="lunch">Midi</option>
            <option value="dinner">Soir</option>
          </select>
          <input aria-label="Rechercher une réservation" placeholder="Nom / téléphone / email" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="bc-resa-admin-kpis">
          {Object.entries(statusCounts).map(([key, value]) => (
            <span key={key}>{STATUS_LABELS[key] || key}: {value}</span>
          ))}
        </div>

        {error ? <p className="bc-resa-admin-error">{error}</p> : null}

        <div className="bc-resa-admin-table-wrap">
          <table className="bc-resa-admin-table">
            <thead>
              <tr>
                <th align="left">Heure</th>
                <th align="left">Client</th>
                <th align="left">Téléphone</th>
                <th align="left">Couverts</th>
                <th align="left">Service</th>
                <th align="left">Statut</th>
                <th align="left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.reservationTime}</td>
                  <td>{item.customerName}</td>
                  <td>{item.customerPhone}</td>
                  <td>{item.partySize}</td>
                  <td>{SERVICE_LABELS[item.service] || item.service}</td>
                  <td><span className={`bc-resa-status is-${item.status}`}>{STATUS_LABELS[item.status] || item.status}</span></td>
                  <td>
                    <div className="bc-resa-admin-actions">
                      <button type="button" className="bc-resa-btn bc-resa-btn-secondary" onClick={() => updateStatus(item, 'confirmed')}>Confirmer</button>
                      <button type="button" className="bc-resa-btn bc-resa-btn-secondary" onClick={() => updateStatus(item, 'seated')}>Installer</button>
                      <button type="button" className="bc-resa-btn bc-resa-btn-secondary" onClick={() => updateStatus(item, 'completed')}>Terminer</button>
                      <button type="button" className="bc-resa-btn bc-resa-btn-danger" onClick={() => updateStatus(item, 'cancelled')}>Annuler</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!items.length ? <p className="bc-resa-admin-empty">Aucune réservation pour ce filtre.</p> : null}
        </div>
      </section>
    </main>
  );
}

export default CaisseReservationsView;
